import { Injectable } from '@angular/core';

export interface ConnectionLogEntry {
  timestamp: Date;
  type: 'connection' | 'disconnection' | 'reconnection' | 'error';
  message: string;
  details?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ConnectionLogService {
  private readonly MAX_LOGS = 100; // Mantener solo los últimos 100 logs
  private readonly STORAGE_KEY = 'sip_connection_logs';

  constructor() {
    // Inicializar localStorage si no existe
    if (!localStorage.getItem(this.STORAGE_KEY)) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify([]));
    }
  }

  /**
   * Agregar un log de conexión exitosa
   */
  logConnection(username?: string) {
    this.addLog({
      type: 'connection',
      message: `✅ Conectado exitosamente${username ? ` (Usuario: ${username})` : ''}`,
      details: { username }
    });
  }

  /**
   * Agregar un log de desconexión
   */
  logDisconnection(reason?: string) {
    this.addLog({
      type: 'disconnection',
      message: `⚠️ Desconectado${reason ? `: ${reason}` : ''}`,
      details: { reason }
    });
  }

  /**
   * Agregar un log de reconexión
   */
  logReconnection(attemptNumber: number, maxAttempts: number) {
    this.addLog({
      type: 'reconnection',
      message: `🔄 Intentando reconexión (${attemptNumber}/${maxAttempts})`,
      details: { attemptNumber, maxAttempts }
    });
  }

  /**
   * Agregar un log de error
   */
  logError(errorMessage: string, errorDetails?: any) {
    this.addLog({
      type: 'error',
      message: `❌ Error: ${errorMessage}`,
      details: errorDetails
    });
  }

  /**
   * Agregar un log genérico
   */
  private addLog(entry: Omit<ConnectionLogEntry, 'timestamp'>) {
    const logs = this.getLogs();

    const newLog: ConnectionLogEntry = {
      ...entry,
      timestamp: new Date()
    };

    logs.unshift(newLog); // Agregar al principio

    // Mantener solo los últimos MAX_LOGS
    if (logs.length > this.MAX_LOGS) {
      logs.splice(this.MAX_LOGS);
    }

    this.saveLogs(logs);
  }

  /**
   * Obtener todos los logs
   */
  getLogs(): ConnectionLogEntry[] {
    try {
      const logsJson = localStorage.getItem(this.STORAGE_KEY);
      if (!logsJson) return [];

      const logs = JSON.parse(logsJson);

      // Convertir timestamps de string a Date
      return logs.map((log: any) => ({
        ...log,
        timestamp: new Date(log.timestamp)
      }));
    } catch (error) {
      console.error('Error al leer logs:', error);
      return [];
    }
  }

  /**
   * Obtener logs filtrados por tipo
   */
  getLogsByType(type: ConnectionLogEntry['type']): ConnectionLogEntry[] {
    return this.getLogs().filter(log => log.type === type);
  }

  /**
   * Obtener logs desde una fecha específica
   */
  getLogsSince(date: Date): ConnectionLogEntry[] {
    return this.getLogs().filter(log => log.timestamp >= date);
  }

  /**
   * Obtener logs de hoy
   */
  getTodayLogs(): ConnectionLogEntry[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.getLogsSince(today);
  }

  /**
   * Limpiar todos los logs
   */
  clearLogs() {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify([]));
  }

  /**
   * Guardar logs en localStorage
   */
  private saveLogs(logs: ConnectionLogEntry[]) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
      console.error('Error al guardar logs:', error);
    }
  }

  /**
   * Exportar logs como texto
   */
  exportLogsAsText(): string {
    const logs = this.getLogs();
    return logs.map(log => {
      const timestamp = log.timestamp.toLocaleString('es-ES');
      const details = log.details ? ` | Detalles: ${JSON.stringify(log.details)}` : '';
      return `[${timestamp}] ${log.message}${details}`;
    }).join('\n');
  }

  /**
   * Obtener estadísticas de conexión
   */
  getConnectionStats() {
    const logs = this.getLogs();
    return {
      total: logs.length,
      connections: logs.filter(l => l.type === 'connection').length,
      disconnections: logs.filter(l => l.type === 'disconnection').length,
      reconnections: logs.filter(l => l.type === 'reconnection').length,
      errors: logs.filter(l => l.type === 'error').length
    };
  }
}

